// Service for user interactions with spots (likes, visits, wishlists)
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

class UserInteractionService {
  constructor() {
    this.baseURL = BASE_URL;
  }

  // Helper method to get auth headers
  getAuthHeaders(token) {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  // Spot interactions
  async likeSpot(spotId, token) {
    try {
      const response = await fetch(`${this.baseURL}/api/user/spots/${spotId}/like`, {
        method: 'POST',
        headers: this.getAuthHeaders(token)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error liking spot:', error);
      throw error;
    }
  }

  async addVisit(spotId, visitData, token) {
    try {
      const response = await fetch(`${this.baseURL}/api/user/spots/${spotId}/visit`, {
        method: 'POST',
        headers: this.getAuthHeaders(token),
        body: JSON.stringify(visitData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding visit:', error);
      throw error;
    }
  }

  async getLikedSpots(token) {
    try {
      const response = await fetch(`${this.baseURL}/api/user/liked-spots`, {
        headers: this.getAuthHeaders(token)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching liked spots:', error);
      throw error;
    }
  }

  async getVisitedSpots(token) {
    try {
      const response = await fetch(`${this.baseURL}/api/user/visited-spots`, {
        headers: this.getAuthHeaders(token)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching visited spots:', error);
      throw error;
    }
  }

  // Photo interactions
  async likePhoto(photoUrl, spotId, token) {
    try {
      const response = await fetch(`${this.baseURL}/api/user/photos/like?photo_url=${encodeURIComponent(photoUrl)}&spot_id=${spotId}`, {
        method: 'POST',
        headers: this.getAuthHeaders(token)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error liking photo:', error);
      throw error;
    }
  }

  async getLikedPhotos(token) {
    try {
      const response = await fetch(`${this.baseURL}/api/user/liked-photos`, {
        headers: this.getAuthHeaders(token)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching liked photos:', error);
      throw error;
    }
  }

  // Wishlist operations
  async createWishlist(wishlistData, token) {
    try {
      const response = await fetch(`${this.baseURL}/api/user/wishlists`, {
        method: 'POST',
        headers: this.getAuthHeaders(token),
        body: JSON.stringify(wishlistData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating wishlist:', error);
      throw error;
    }
  }

  async getWishlists(token) {
    try {
      const response = await fetch(`${this.baseURL}/api/user/wishlists`, {
        headers: this.getAuthHeaders(token)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching wishlists:', error);
      throw error;
    }
  }

  async updateWishlist(wishlistId, updateData, token) {
    try {
      const response = await fetch(`${this.baseURL}/api/user/wishlists/${wishlistId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(token),
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating wishlist:', error);
      throw error;
    }
  }

  async addSpotToWishlist(wishlistId, spotId, token) {
    try {
      const response = await fetch(`${this.baseURL}/api/user/wishlists/${wishlistId}/spots/${spotId}`, {
        method: 'POST',
        headers: this.getAuthHeaders(token)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding spot to wishlist:', error);
      throw error;
    }
  }

  async removeSpotFromWishlist(wishlistId, spotId, token) {
    try {
      const response = await fetch(`${this.baseURL}/api/user/wishlists/${wishlistId}/spots/${spotId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(token)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing spot from wishlist:', error);
      throw error;
    }
  }

  // Contributions
  async getContributions(token) {
    try {
      const response = await fetch(`${this.baseURL}/api/user/contributions`, {
        headers: this.getAuthHeaders(token)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching contributions:', error);
      throw error;
    }
  }

  // Public Profiles
  async getPublicProfile(username) {
    try {
      const response = await fetch(`${this.baseURL}/api/user/profile/${username}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching public profile:', error);
      throw error;
    }
  }

  async searchUsers(query) {
    try {
      const response = await fetch(`${this.baseURL}/api/user/search/users?q=${encodeURIComponent(query)}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }
}

// Create a singleton instance
const userInteractionService = new UserInteractionService();

export default userInteractionService;